import React, { useState } from 'react';
import { Select, AdvancedFilter, DateRangePicker, Input, DatePicker } from '@kaleyra/emmy';
import { InjectedPropsType } from '@kaleyra/emmy/dist/molecules/advancedFilter';
import moment from 'moment';
import Styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { IOption } from '../index';

type SelfProps = {
	statusOptions: IOption[];
	conversationCategories: IOption[];
	formatOptions: IOption[];
	logsFor: string;
	callType: IOption[];
	voiceStatusOptions: IOption[];
};

type FilterPropsType = Omit<
	React.ComponentProps<typeof AdvancedFilter>,
	'children'
> &
	SelfProps;

type FieldNameType = 'format' | 'status' | 'created' | 'conversation_type' | 'call_type' | 'from_number' | 'to_number' | 'call_status' | 'start_date' | 'call_id';

const LogFilter: React.FC<FilterPropsType> = ({
	appliedFilters,
	onFilter,
	conversationCategories,
	statusOptions,
	formatOptions,
	logsFor,
	callType,
	voiceStatusOptions,
}) => {
	const { t } = useTranslation();

	const [filterDText, setFilterDText] = useState<any>('');
	const [filterVoiceDate,setFilterVoiceDate] = useState<any>('')

	let updatedCreatedDate = filterDText;
	if (filterDText) {
		let createdArr = filterDText.split(',');
		let startDate = moment(createdArr[0], 'YYYY-MM-DD HH:mm:ss');
		let endDate = moment(createdArr[1], 'YYYY-MM-DD HH:mm:ss');
		updatedCreatedDate = [startDate, endDate];
	}

	let startFullDate = filterVoiceDate;
	if (filterVoiceDate) {
		let createdArr = filterVoiceDate.split(',');
		let startDate = moment(createdArr[0], 'YYYY-MM-DD');
		let endDate = moment(createdArr[1], 'YYYY-MM-DD');
		startFullDate = [startDate, endDate];
	}

	// let start_date = filterStartD;
	// if (filterStartD) {
	// 	start_date = moment(new Date(filterStartD))?.format('YYYY-MM-DD');
	// }

	const onChange = (
		changeFilters: (filters: Record<string, any>) => void,
		filters: any
	) => (field: FieldNameType, custom?: boolean) => (value: any) => {
		const newFilters = {
			...filters,
			[field]: custom
				? (value as React.ChangeEvent<HTMLInputElement>).target.value
				: value,
		};
		changeFilters(newFilters);
	};

	const onDateChange = (
		changeFilters: (filters: Record<string, any>) => void,
		filters: any
	) => (field: FieldNameType, custom?: boolean)  => {
		if (custom && field === 'created') {
			setFilterDText(null);
			const newFilters = {
				...filters,
				[field]: null,
			};
			changeFilters(newFilters);
		}
		else if(field === 'start_date' && custom){
			setFilterVoiceDate(null);
			const newFilters = {
				...filters,
				[field]: null,
			};
			changeFilters(newFilters);
		}
		else {
			return (value: any) => {
				const [start, end] = value;
				if (start && end) {
					switch(logsFor){
						case 'voiceLogs':
							const formattedVoiceDate = `${start
                                .startOf('day')
                                .format('YYYY-MM-DD')},${end
                                .endOf('day')
                                .format('YYYY-MM-DD')}`;
                            setFilterVoiceDate(formattedVoiceDate);
                            const updatedFilters = {
                                ...filters,
                                [field]: formattedVoiceDate,
                            };
                            changeFilters(updatedFilters);
                            break;
						case 'messageLogs':
							const formattedDate = `${start
								.startOf('day')
								.format('YYYY-MM-DD HH:mm:ss')},${end
								.endOf('day')
								.format('YYYY-MM-DD HH:mm:ss')}`;
							setFilterDText(formattedDate);
							const timeStamp = `${start.unix()},${end.unix()}`;
							const newFilters = {
								...filters,
								[field]: timeStamp,
							};
							changeFilters(newFilters);
							break;
					}
				}
			};
		}
	};

	const getFilters = ({
		changeFilters,
		filters,
	}: InjectedPropsType): JSX.Element => {
		const onFilterChange = onChange(changeFilters, filters);

		const onDateFilterChange = onDateChange(changeFilters, filters);

		return (
			<div className={Styles.container}>
				<div>
					<Select
						placeholder={t('conversationCategoryLabel')}
						label={t('conversationCategoryLabel')}
						value={filters?.conversation_type}
						onChange={onFilterChange('conversation_type')}
						options={conversationCategories}
						showBottomLabelSpace={false}
						className={Styles.inputField}
					/>
				</div>
				<div>
					<Select
						placeholder={t('BLD00963')}
						label={t('BLD00963')}
						value={filters?.format}
						onChange={onFilterChange('format')}
						options={formatOptions}
						showBottomLabelSpace={false}
						className={Styles.inputField}
					/>
				</div>
				<div>
					<Select
						placeholder={t('RBC2042')}
						label={t('RBC2042')}
						value={filters?.status}
						onChange={onFilterChange('status')}
						options={statusOptions}
						showBottomLabelSpace={false}
						className={Styles.inputField}
					/>
				</div>
				<div>
					<span className={Styles.dateLabel}>
						{t('KLR0035')}
					</span>
					<DateRangePicker
						placeholder={[t('BLD00530'), t('BLD00151')]}
						onOk={onDateFilterChange('created')}
						value={filters?.created ? updatedCreatedDate : ''}
						formatForDatePicker={'DD MMM YYYY'}
						style={{ width: '100%', marginTop: '0.25rem' }}
						disabledDate={(current: any) => {
							return current && current > moment().endOf('day');
						}}
						onClear={() => onDateFilterChange('created', true)}
						showBottomLabelSpace={false}
					/>
				</div>
			</div>
		);
	};

	const getVoiceFilters = ({
		changeFilters,
		filters,
	}: InjectedPropsType): JSX.Element => {
		const onFilterChange = onChange(changeFilters, filters);

		const onDateFilterChange = onDateChange(changeFilters, filters);

		return (
			<div className={Styles.container}>
				<div>
					<Select
						placeholder={t('voiceLogCallType')}
						label={t('voiceLogCallType')}
						value={filters?.call_type}
						onChange={onFilterChange('call_type')}
						options={callType}
						showBottomLabelSpace={false}
						className={Styles.inputField}
					/>
				</div>
				<div>
					<Input
						placeholder={t('fromNumber')}
						label={t('fromNumber')}
						value={filters?.from_number}
						onChange={onFilterChange( 'from_number', true)}
					/>
				</div>
				<div>
					<Input
						placeholder={t('toNum')}
						label={t('toNum')}
						value={filters?.to_number}
						onChange={onFilterChange( 'to_number', true)}
					/>
				</div>
				<div>
					<Select
						placeholder={t('voiceLogCallStatus')}
						label={t('voiceLogCallStatus')}
						value={filters?.call_status}
						onChange={onFilterChange('call_status')}
						options={voiceStatusOptions}
						showBottomLabelSpace={false}
						className={Styles.inputField}
					/>
				</div>
				{/* <div>
					<span className={Styles.dateLabel}>
						{t('KLR0035')}
					</span>
					<DateRangePicker
						placeholder={[t('BLD00530'), t('BLD00151')]}
						onOk={onDateFilterChange('start_date')}
						value={filters?.start_date ? startFullDate : ''}
						formatForDatePicker={'DD MMM YYYY'}
						style={{ width: '100%', marginTop: '0.25rem' }}
						disabledDate={(current: any) => {
							return current && current > moment().endOf('day');
						}}
						onClear={() => onDateFilterChange('start_date', true)}
						showBottomLabelSpace={false}
					/>
				</div> */}
				<div>
					<span className={Styles.dateLabel}>
						{t('KLR0035')}
					</span>
					<DateRangePicker
						placeholder={[t('BLD00530'), t('BLD00151')]}
						onOk={onDateFilterChange('start_date')}
						value={filters?.start_date ? startFullDate : ''}
						formatForDatePicker={'DD MMM YYYY'}
						style={{ width: '100%', marginTop: '0.25rem' }}
						disabledDate={(current: any) => {
							return current && current > moment().endOf('day');
						}}
						onClear={() => onDateFilterChange('start_date', true)}
						showBottomLabelSpace={false}
					/>
				</div>
			</div>
		);
	};

	return (
		<AdvancedFilter
			appliedFilters={appliedFilters}
			onFilter={onFilter}
			filterTitle={t('Filters')}
			cancelButtonText={t('mainCancel')}
			confirmButtonText={t('BLD01036')}
		>
			{logsFor == "voiceLogs"? getVoiceFilters: getFilters}
		</AdvancedFilter>
	);
};

export default LogFilter;
